import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import { parseTagColor } from '../../lib/boardInvariants'
import {
  PlayerTagCreateDocument,
  PlayerTagDeleteDocument,
  PlayerTagRenameDocument,
  PlayerTagSetColorDocument,
  PlayerTagsDocument,
} from '../../lib/handWrittenGraphql'
import type {
  PlayerTagCreateMutation,
  PlayerTagDeleteMutation,
  PlayerTagRenameMutation,
  PlayerTagSetColorMutation,
} from '../../lib/handWrittenGraphql'
import type { PlayerTag } from '../../types'
import { MAX_NAME_LEN, TagColor } from '../../types'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

function isValidRaidNameLocal(name: string): boolean {
  const trimmed = name.trim()
  if (trimmed === '') return false
  if (Array.from(trimmed).length > 80) return false
  return true
}

export interface TagActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly dbGuildId: Ref<number | null>
  readonly error: Ref<string | null>
  readonly playerTags: Ref<ReadonlyArray<PlayerTag>>
  readonly activeTagFilter: Ref<string | null>
  readonly resolveDbGuildId: () => Promise<number | null>
}

/**
 * Catalog CRUD surface for player tags. The per-player assignment
 * wiring (`assignTag` / `unassignTag` / `setActiveTagFilter` /
 * `isActiveTagFilter`) was moved to `stores/tags/assignments.ts` in
 * Phase 0; the bulk-reset orchestration moved to `stores/tags/reset.ts`.
 * Each of those composables is instantiated by `gvgPlannerStore.ts` and
 * merged into the same flat public-API surface.
 */
export interface TagActionsApi {
  readonly fetchPlayerTags: () => Promise<void>
  readonly createTag: (name: string, color: string | null) => Promise<boolean>
  readonly renameTag: (tagId: string, name: string) => Promise<boolean>
  readonly setTagColor: (tagId: string, color: string) => Promise<boolean>
  readonly deleteTag: (tagId: string) => boolean
}

export function useTagActions(ctx: TagActionsCtx): TagActionsApi {
  let lastRequestedGuildId: string | null = null

  async function fetchPlayerTags(): Promise<void> {
    const guildId = ctx.activeDiscordGuildId.value
    if (guildId === '0') {
      lastRequestedGuildId = guildId
      ctx.playerTags.value = []
      ctx.activeTagFilter.value = null
      return
    }
    if (lastRequestedGuildId !== guildId) {
      lastRequestedGuildId = guildId
      ctx.playerTags.value = []
      ctx.activeTagFilter.value = null
    }
    try {
      let id = ctx.dbGuildId.value
      if (id === null) {
        id = await ctx.resolveDbGuildId()
      }
      if (id === null) return
      const res = await apolloClient.query<{
        playerTags: {
          edges: ReadonlyArray<{ node: { id: number; guildId: number; name: string; seedKey: string | null; color: string } }>
        }
      }>({
        query: PlayerTagsDocument,
        variables: { guildId: id },
        fetchPolicy: 'network-only',
      })
      if (guildId !== ctx.activeDiscordGuildId.value) return
      const edges = res.data?.playerTags.edges ?? []
      const list: PlayerTag[] = []
      for (const e of edges) {
        const t = e.node
        list.push({
          id: String(t.id),
          guildId: String(t.guildId),
          name: t.name,
          seedKey: t.seedKey ?? null,
          color: parseTagColor(t.color),
        })
      }
      ctx.playerTags.value = list
    } catch (e: unknown) {
      if (guildId !== ctx.activeDiscordGuildId.value) return
      ctx.error.value = e instanceof Error ? e.message : String(e)
    }
  }

  async function createTag(name: string, color: string | null): Promise<boolean> {
    if (!isValidRaidNameLocal(name)) {
      ctx.error.value = `Enter a tag name between 1 and ${String(MAX_NAME_LEN)} characters.`
      return false
    }
    return (async (): Promise<boolean> => {
      try {
        const res = await apolloClient.mutate<PlayerTagCreateMutation>({
          mutation: PlayerTagCreateDocument,
          variables: {
            guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
            name,
            color,
          } as never,
        })
        const created = res.data?.playerTagCreate
        if (!created) return false
        return true
      } catch (e: unknown) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
        return false
      }
    })()
  }

  async function renameTag(tagId: string, name: string): Promise<boolean> {
    if (!isValidRaidNameLocal(name)) {
      ctx.error.value = `Enter a tag name between 1 and ${String(MAX_NAME_LEN)} characters.`
      return false
    }
    return (async (): Promise<boolean> => {
      try {
        const res = await apolloClient.mutate<PlayerTagRenameMutation>({
          mutation: PlayerTagRenameDocument,
          variables: {
            guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
            tagId: Number(tagId),
            name,
          } as never,
        })
        if (res.data?.playerTagRename === true) {
          return true
        }
        return false
      } catch (e: unknown) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
        return false
      }
    })()
  }

  async function setTagColor(tagId: string, color: string): Promise<boolean> {
    return (async (): Promise<boolean> => {
      try {
        const res = await apolloClient.mutate<PlayerTagSetColorMutation>({
          mutation: PlayerTagSetColorDocument,
          variables: {
            guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
            tagId: Number(tagId),
            color,
          } as never,
        })
        if (res.data?.playerTagSetColor === true) {
          return true
        }
        return false
      } catch (e: unknown) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
        return false
      }
    })()
  }

  function deleteTag(tagId: string): boolean {
    void (async (): Promise<void> => {
      try {
        const res = await apolloClient.mutate<PlayerTagDeleteMutation>({
          mutation: PlayerTagDeleteDocument,
          variables: {
            guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
            tagId: Number(tagId),
          } as never,
        })
        if (res.data?.playerTagDelete === true) {
          if (ctx.activeTagFilter.value === tagId) ctx.activeTagFilter.value = null
        }
      } catch (e: unknown) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
      }
    })()
    return true
  }

  return {
    fetchPlayerTags,
    createTag,
    renameTag,
    setTagColor,
    deleteTag,
  }
}

void TagColor
